import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import {
  Autocomplete,
  Box,
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  TextField,
  IconButton,
  Tooltip,
  CircularProgress
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import { Search } from 'lucide-react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { parseYmdToDayjs, formatDayjsToYmd } from '../utils/adminDateUtils.js';
import { adminDateFieldSx } from '../components/AdminDatePicker.jsx';
import { adminAPI, eventBatchAPI, trekAPI } from '../services/api';
import { useToast } from '../context/ToastContext';
import useListQueryParams from '../hooks/useListQueryParams';
import EditRegistrationModal from '../components/EditRegistrationModal.jsx';
import { formatPersonDisplayName } from '../utils/capitalizePersonName.js';
import EditTicketGroupModal from '../components/EditTicketGroupModal.jsx';
import { sortEventBatchesByStartDateAsc } from '../utils/sortEventBatchesByStartDateDesc.js';
import { fetchAllEventBatches } from '../utils/fetchAllEventBatches.js';
import { exportTicketsCsvForFilters } from '../utils/ticketsExportCsv.js';
import { formatTicketGenderAge } from '../utils/ticketGenderAge.js';
import { resolveTicketPnrs, resolveLatestTicketUploadDate } from '../utils/ticketPnr.js';
import { ticketPackageSubDetail } from '../utils/ticketPackage.js';
import AdminDateRangeFilter from '../components/AdminDateRangeFilter.jsx';
import { PageHeader, FilterBar, EmptyState } from '../components/ui';
import './Registrations.css';

function formatDate(d) {
  if (!d) return '—';
  const x = new Date(d);
  return isNaN(x.getTime()) ? '—' : x.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatCreatedAt(d) {
  if (!d) return '—';
  const x = new Date(d);
  if (isNaN(x.getTime())) return '—';
  return x.toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: 'Asia/Kolkata'
  });
}

const TICKET_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending', bg: '#fef3c7', color: '#92400e', border: '#fde68a' },
  { value: 'ticket_booked', label: 'Ticket Booked', bg: '#d1fae5', color: '#065f46', border: '#a7f3d0' },
  { value: 'no_response', label: 'No Response', bg: '#ffe4e6', color: '#be123c', border: '#fecdd3' },
  { value: 'on_hold', label: 'On Hold', bg: '#dbeafe', color: '#1e40af', border: '#bfdbfe' },
  { value: 'ticket_not_open', label: 'Ticket Not Open', bg: '#f1f5f9', color: '#475569', border: '#cbd5e1' },
  { value: 'cancelled', label: 'Cancel', bg: '#fee2e2', color: '#991b1b', border: '#fecaca' }
];

function getTicketStatusStyle(status) {
  const norm = String(status || '').trim().toLowerCase();
  if (norm === 'cancel') return TICKET_STATUS_OPTIONS.find((opt) => opt.value === 'cancelled');
  const hit = TICKET_STATUS_OPTIONS.find((opt) => opt.value === norm);
  if (hit) return hit;
  return { value: norm, label: norm || 'Pending', bg: '#fef3c7', color: '#92400e', border: '#fde68a' };
}

const Tickets = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [editModal, setEditModal] = useState({ open: false, bookingId: null });
  const [groupModal, setGroupModal] = useState({ open: false, row: null });
  const [exportingCsv, setExportingCsv] = useState(false);
  const [updatingStatusId, setUpdatingStatusId] = useState(null);

  const { query, setQuery } = useListQueryParams({
    page: '1',
    limit: '20',
    trekId: '',
    batchId: '',
    search: '',
    fromDate: '',
    toDate: '',
    sortOrder: 'desc',
    includeCancelled: '',
    showAll: '',
    ticketStatus: '',
    pnrStatus: 'pending_pnrs'
  });
  const trekFilter = query.trekId || '';
  const batchFilter = query.batchId || '';
  const search = query.search || '';
  const page = Number(query.page || 1);
  const limit = Number(query.limit || 20);
  const fromDate = query.fromDate || '';
  const toDate = query.toDate || '';
  const sortOrder = query.sortOrder || 'desc';
  const includeCancelled = query.includeCancelled === 'true';
  const showAll = query.showAll === 'true';
  const ticketStatusFilter = query.ticketStatus || '';
  const pnrStatusFilter = query.pnrStatus || 'pending_pnrs';

  const batchListParams = useMemo(
    () => ({
      sortBy: 'startDate',
      sortOrder: 'asc',
      noCount: 'true',
      ...(showAll ? { pickerOnly: 'true' } : { upcomingOnly: 'true', limit: 500 })
    }),
    [showAll]
  );

  const { data: batchesData, isFetching: batchesLoading } = useQuery({
    queryKey: ['admin-event-batches-tickets', trekFilter, showAll, batchListParams],
    queryFn: async () => {
      const base = { ...batchListParams, trekId: trekFilter };
      if (showAll) {
        const rows = await fetchAllEventBatches(base);
        return { data: { data: rows } };
      }
      return eventBatchAPI.list(base);
    },
    enabled: Boolean(trekFilter)
  });
  const allBatches = batchesData?.data?.data || batchesData?.data || [];
  const batches = sortEventBatchesByStartDateAsc(
    trekFilter ? allBatches.filter((b) => String(b.trekId || b.trek?.id) === String(trekFilter)) : []
  );

  const { data: allTreksData } = useQuery({
    queryKey: ['admin-treks-for-tickets', 'all'],
    queryFn: () => trekAPI.list({ page: 1, limit: 500 }),
    enabled: showAll
  });

  const { data: upcomingBatchesForTreksData } = useQuery({
    queryKey: ['admin-event-batches-for-tickets-trek-filter', showAll],
    queryFn: () => eventBatchAPI.list(batchListParams),
    enabled: !showAll
  });

  const upcomingTreks = useMemo(() => {
    const rows = upcomingBatchesForTreksData?.data?.data || upcomingBatchesForTreksData?.data || [];
    const byId = new Map();
    for (const b of rows) {
      const t = b.trek;
      if (t?.id == null) continue;
      const key = String(t.id);
      if (!byId.has(key)) {
        byId.set(key, { id: t.id, name: t.name, slug: t.slug });
      }
    }
    return [...byId.values()].sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
  }, [upcomingBatchesForTreksData]);

  const treks = showAll ? allTreksData?.data?.data || [] : upcomingTreks;
  const isValidTrekFilter = Boolean(trekFilter && treks.some((t) => String(t.id) === String(trekFilter)));
  const isValidBatchFilter = Boolean(batchFilter && batches.some((b) => String(b.id) === String(batchFilter)));

  const buildFilterParams = () => {
    const params = {};
    if (showAll) params.includePastEvents = 'true';
    if (includeCancelled) params.includeCancelled = 'true';
    if (isValidTrekFilter && isValidBatchFilter) {
      params.trekId = trekFilter;
      params.batchId = batchFilter;
    } else if (isValidTrekFilter) {
      params.trekId = trekFilter;
    } else if (isValidBatchFilter) {
      params.batchId = batchFilter;
    }
    if (search.trim()) params.search = search.trim();
    if (fromDate) params.fromDate = fromDate;
    if (toDate) params.toDate = toDate;
    if (sortOrder) params.sortOrder = sortOrder;
    if (ticketStatusFilter) params.ticketStatus = ticketStatusFilter;
    if (pnrStatusFilter) params.pnrStatus = pnrStatusFilter;
    return params;
  };

  const { data, isLoading } = useQuery({
    queryKey: [
      'admin-ticket-bookings',
      trekFilter,
      batchFilter,
      search,
      fromDate,
      toDate,
      sortOrder,
      includeCancelled,
      showAll,
      ticketStatusFilter,
      pnrStatusFilter,
      page,
      limit
    ],
    queryFn: () => adminAPI.getTicketBookings({ ...buildFilterParams(), page, limit }),
    staleTime: 30_000,
    keepPreviousData: true
  });

  const list = data?.data?.data || [];
  const pagination = data?.data?.pagination || {};
  const total = pagination.total ?? list.length;
  const stats = data?.data?.stats || {
    totalTickets: 0,
    pendingTickets: 0,
    onHoldTickets: 0,
    noResponseTickets: 0,
    bookedToday: 0
  };

  const handleStatusChange = async (row, newStatus) => {
    if (!row?.id || updatingStatusId) return;
    setUpdatingStatusId(row.id);
    try {
      await adminAPI.patchAdminBooking(row.id, { ticketStatus: newStatus });
      const opt = TICKET_STATUS_OPTIONS.find((o) => o.value === newStatus);
      showToast({ message: `Ticket status updated to ${opt?.label || newStatus}`, severity: 'success' });
      await queryClient.invalidateQueries('admin-ticket-bookings');
    } catch (err) {
      showToast({
        message: err.response?.data?.message || err.message || 'Failed to update ticket status.',
        severity: 'error'
      });
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const openEditRegistration = (row) => {
    if (!row?.id) {
      showToast({ message: 'Missing booking id for this row.', severity: 'warning' });
      return;
    }
    setEditModal({ open: true, bookingId: row.id });
  };

  const openTicketGroupModal = (row) => {
    if (!row?.id) {
      showToast({ message: 'Missing booking for this row.', severity: 'warning' });
      return;
    }
    setGroupModal({ open: true, row });
  };

  const handleExportTicketsCsv = async () => {
    setExportingCsv(true);
    try {
      const { rowCount, truncated } = await exportTicketsCsvForFilters(buildFilterParams());
      const rowPart = rowCount != null ? `${rowCount} row(s)` : 'CSV';
      const dateHint =
        fromDate || toDate
          ? ` for ${fromDate || '…'} to ${toDate || '…'}`
          : '';
      showToast({
        message: truncated
          ? `Downloaded ${rowPart}${dateHint} (export capped at 25,000 bookings — narrow filters for full export).`
          : `Downloaded ${rowPart}${dateHint} for all ${total} matching booking(s).`,
        severity: truncated ? 'warning' : 'success',
        duration: truncated ? 8000 : 5000
      });
    } catch (err) {
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          showToast({ message: parsed.message || 'CSV export failed.', severity: 'error' });
        } catch {
          showToast({ message: 'CSV export failed.', severity: 'error' });
        }
      } else {
        const msg = err.response?.data?.message || err.message || 'CSV export failed.';
        showToast({ message: msg, severity: 'error' });
      }
    } finally {
      setExportingCsv(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Pending tickets (registrations)"
        actions={
          <Button
            variant="contained"
            disabled={exportingCsv || total === 0}
            onClick={handleExportTicketsCsv}
          >
            {exportingCsv ? 'Downloading CSV…' : `Download tickets CSV (${total})`}
          </Button>
        }
      />

      {/* Summary Metrics Card */}
      <Card
        sx={{
          mb: 3,
          p: 0,
          borderRadius: 2.5,
          border: '1px solid #e2e8f0',
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' },
            divider: '1px solid #f1f5f9'
          }}
        >
          {/* Total Tickets */}
          <Box
            sx={{
              p: 2.5,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              borderRight: { md: '1px solid #e2e8f0' },
              borderBottom: { xs: '1px solid #e2e8f0', md: 'none' }
            }}
          >
            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Total Tickets
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a' }}>
              {Number(stats.totalTickets ?? 0).toLocaleString('en-IN')}
            </Typography>
            <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.72rem' }}>
              Pending + On Hold + No Response + Booked Today
            </Typography>
          </Box>

          {/* Pending Tickets */}
          <Box
            sx={{
              p: 2.5,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              borderRight: { sm: 'none', md: '1px solid #e2e8f0' },
              borderBottom: { xs: '1px solid #e2e8f0', md: 'none' }
            }}
          >
            <Typography variant="caption" sx={{ color: '#d97706', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Pending Tickets
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#d97706' }}>
              {Number(stats.pendingTickets ?? 0).toLocaleString('en-IN')}
            </Typography>
            <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.72rem' }}>
              All tickets with pending status
            </Typography>
          </Box>

          {/* On Hold Tickets */}
          <Box
            sx={{
              p: 2.5,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              borderRight: { md: '1px solid #e2e8f0' },
              borderBottom: { xs: '1px solid #e2e8f0', sm: 'none', md: 'none' }
            }}
          >
            <Typography variant="caption" sx={{ color: '#2563eb', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              On Hold Tickets
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#2563eb' }}>
              {Number(stats.onHoldTickets ?? 0).toLocaleString('en-IN')}
            </Typography>
            <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.72rem' }}>
              Held / waiting for update
            </Typography>
          </Box>

          {/* No Response */}
          <Box
            sx={{
              p: 2.5,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              borderRight: { md: '1px solid #e2e8f0' },
              borderBottom: { xs: '1px solid #e2e8f0', sm: 'none', md: 'none' }
            }}
          >
            <Typography variant="caption" sx={{ color: '#be123c', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              No Response
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#be123c' }}>
              {Number(stats.noResponseTickets ?? 0).toLocaleString('en-IN')}
            </Typography>
            <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.72rem' }}>
              Customer not responding
            </Typography>
          </Box>

          {/* Ticket Booked Today */}
          <Box
            sx={{
              p: 2.5,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5
            }}
          >
            <Typography variant="caption" sx={{ color: '#059669', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Ticket Booked Today
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#059669' }}>
              {Number(stats.bookedToday ?? 0).toLocaleString('en-IN')}
            </Typography>
            <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.72rem' }}>
              Confirmed today (daily dynamic)
            </Typography>
          </Box>
        </Box>
      </Card>

      {/* Filter Card (2-row grid: Row 1 = Trek & Event, Row 2 = Ticket Status, Paid from, Paid to) */}
      <FilterBar contentSx={{ py: 2.5, px: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, width: '100%' }}>
          {/* Row 1: Trek and Event field */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 2.5,
              width: '100%',
              alignItems: 'center'
            }}
          >
            {/* Trek */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%' }}>
              <Autocomplete
                size="small"
                fullWidth
                options={treks}
                getOptionLabel={(t) => t?.name ?? ''}
                isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
                value={treks.find((t) => String(t.id) === String(trekFilter)) ?? null}
                onChange={(_, trek) => setQuery({ trekId: trek ? String(trek.id) : '', batchId: '', page: '1' })}
                noOptionsText="No treks found"
                ListboxProps={{ style: { maxHeight: 320 } }}
                renderInput={(params) => <TextField {...params} label="Trek" placeholder="Search trek…" />}
              />
              {trekFilter ? (
                <IconButton
                  size="small"
                  aria-label="Clear trek"
                  onClick={() => setQuery({ trekId: '', batchId: '', page: '1' })}
                >
                  <CloseRoundedIcon fontSize="small" />
                </IconButton>
              ) : null}
            </Box>

            {/* Event */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%' }}>
              <Autocomplete
                size="small"
                fullWidth
                disabled={!trekFilter}
                loading={batchesLoading}
                options={batches}
                getOptionLabel={(b) => (b ? `${formatDate(b.startDate)} to ${formatDate(b.endDate)}` : '')}
                isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
                value={batches.find((b) => String(b.id) === String(batchFilter)) ?? null}
                onChange={(_, batch) => setQuery({ batchId: batch ? String(batch.id) : '', page: '1' })}
                noOptionsText={
                  trekFilter ? (batchesLoading ? 'Loading events…' : 'No events found') : 'Select trek first'
                }
                ListboxProps={{ style: { maxHeight: 320 } }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Event"
                    placeholder={
                      trekFilter ? (batchesLoading ? 'Loading events…' : 'Pick date batch…') : 'Select trek first'
                    }
                  />
                )}
              />
              {batchFilter ? (
                <IconButton size="small" aria-label="Clear event" onClick={() => setQuery({ batchId: '', page: '1' })}>
                  <CloseRoundedIcon fontSize="small" />
                </IconButton>
              ) : null}
            </Box>
          </Box>

          {/* Row 2: Ticket Status, Paid from, Paid to */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
              gap: 2.5,
              width: '100%',
              alignItems: 'center'
            }}
          >
            {/* Ticket Status */}
            <FormControl size="small" fullWidth>
              <InputLabel>Ticket Status</InputLabel>
              <Select
                value={ticketStatusFilter}
                label="Ticket Status"
                onChange={(e) => setQuery({ ticketStatus: e.target.value, page: '1' })}
              >
                <MenuItem value="">Pending (or Missing PNR)</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="ticket_booked">Ticket Booked</MenuItem>
                <MenuItem value="no_response">No Response</MenuItem>
                <MenuItem value="on_hold">On Hold</MenuItem>
                <MenuItem value="ticket_not_open">Ticket Not Open</MenuItem>
                <MenuItem value="cancelled">Cancel</MenuItem>
                <MenuItem value="all">All Statuses</MenuItem>
              </Select>
            </FormControl>

            {/* Paid from */}
            <DatePicker
              label="Paid from"
              value={parseYmdToDayjs(fromDate)}
              maxDate={parseYmdToDayjs(toDate) || undefined}
              onChange={(v) => setQuery({ fromDate: formatDayjsToYmd(v) || '', page: '1' })}
              format="DD/MM/YYYY"
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                  sx: adminDateFieldSx
                },
                htmlInput: { 'aria-label': 'Paid from' },
                field: { clearable: true }
              }}
            />

            {/* Paid to */}
            <DatePicker
              label="Paid to"
              value={parseYmdToDayjs(toDate)}
              minDate={parseYmdToDayjs(fromDate) || undefined}
              onChange={(v) => setQuery({ toDate: formatDayjsToYmd(v) || '', page: '1' })}
              format="DD/MM/YYYY"
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                  sx: adminDateFieldSx
                },
                htmlInput: { 'aria-label': 'Paid to' },
                field: { clearable: true }
              }}
            />
          </Box>
        </Box>
      </FilterBar>

      <Card className="ops-table-card">
        {/* Card Header Toolbar: Show all treks toggle and search field at top right corner */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 2.5,
            p: 2,
            borderBottom: '1px solid #f1f5f9',
            flexWrap: 'wrap'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <label className="reg-show-all-label" style={{ margin: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontWeight: 500, fontSize: '0.875rem', color: '#475569' }}>
              <input
                type="checkbox"
                checked={showAll}
                onChange={(e) =>
                  setQuery({
                    showAll: e.target.checked ? 'true' : '',
                    trekId: '',
                    batchId: '',
                    page: '1'
                  })
                }
              />
              Show all treks
            </label>
          </Box>

          <TextField
            size="small"
            placeholder="Search booking ID, name, phone…"
            value={search}
            onChange={(e) => setQuery({ search: e.target.value, page: '1' })}
            sx={{
              minWidth: { xs: '100%', sm: 280 },
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: '#f8fafc'
              }
            }}
            InputProps={{
              startAdornment: <Search size={16} style={{ marginRight: 8, color: '#94a3b8', flexShrink: 0 }} />
            }}
          />
        </Box>
        {isLoading ? (
          <div className="ops-loading-box">
            <CircularProgress size={32} sx={{ color: 'primary.main' }} />
            <Typography variant="body2" color="text.secondary">Loading tickets…</Typography>
          </div>
        ) : list.length === 0 ? (
          <EmptyState
            title="No ticket bookings found"
            description="No ticket-required bookings match these filters."
          />
        ) : (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 1200 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#475569', bgcolor: '#f8fafc' }}>Id</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#475569', bgcolor: '#f8fafc' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#475569', bgcolor: '#f8fafc' }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#475569', bgcolor: '#f8fafc' }}>Trek</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#475569', bgcolor: '#f8fafc' }}>Package</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#475569', bgcolor: '#f8fafc' }}>Start date</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#475569', bgcolor: '#f8fafc' }}>Paid at</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#475569', bgcolor: '#f8fafc' }}>Gender / Age</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#475569', bgcolor: '#f8fafc' }}>DPNR</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#475569', bgcolor: '#f8fafc' }}>RPNR</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#475569', bgcolor: '#f8fafc' }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {list.map((row) => {
                    const trek = row.batch?.trek;
                    const pkg = row.ticketPackage;
                    const pkgDescFull = ticketPackageSubDetail(row);
                    const pkgDescPreview =
                      pkgDescFull.length > 160 ? `${pkgDescFull.slice(0, 160).trim()}…` : pkgDescFull;
                    const primary = Array.isArray(row.travelers) && row.travelers[0] ? row.travelers[0] : null;
                    const fullName = formatPersonDisplayName(primary?.firstName, primary?.lastName);
                    const dob =
                      primary?.birthdate || primary?.dob || row.registration?.dob || null;
                    const { gender, age } = formatTicketGenderAge(
                      primary?.gender || row.registration?.gender,
                      dob
                    );
                    const paidAt = row.paidAt;
                    const { dpnr, rpnr } = resolveTicketPnrs(row);
                    const currentTicketStatus = row.ticketStatus || 'pending';
                    const statusStyle = getTicketStatusStyle(currentTicketStatus);
                    const isRowUpdating = updatingStatusId === row.id;
                    const latestUploadAt = resolveLatestTicketUploadDate(row);

                    return (
                      <TableRow key={row.id} hover>
                        <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{row.registrationId ?? '—'}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Select
                            size="small"
                            value={
                              currentTicketStatus === 'cancel'
                                ? 'cancelled'
                                : TICKET_STATUS_OPTIONS.some((o) => o.value === currentTicketStatus)
                                  ? currentTicketStatus
                                  : 'pending'
                            }
                            disabled={isRowUpdating}
                            onChange={(e) => handleStatusChange(row, e.target.value)}
                            IconComponent={KeyboardArrowDownRoundedIcon}
                            sx={{
                              fontSize: '0.78rem',
                              fontWeight: 700,
                              borderRadius: '20px',
                              height: 28,
                              backgroundColor: statusStyle.bg,
                              color: statusStyle.color,
                              border: `1px solid ${statusStyle.border}`,
                              '.MuiOutlinedInput-notchedOutline': { border: 'none' },
                              '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 'none' },
                              '.MuiSelect-icon': { color: statusStyle.color, fontSize: '1.1rem' },
                              '.MuiSelect-select': { py: 0, px: 1.25, display: 'flex', alignItems: 'center' }
                            }}
                          >
                            <MenuItem value="pending" sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#92400e' }}>
                              Pending
                            </MenuItem>
                            <MenuItem value="ticket_booked" sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#065f46' }}>
                              Ticket Booked
                            </MenuItem>
                            <MenuItem value="no_response" sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#be123c' }}>
                              No Response
                            </MenuItem>
                            <MenuItem value="on_hold" sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#1e40af' }}>
                              On Hold
                            </MenuItem>
                            <MenuItem value="ticket_not_open" sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#475569' }}>
                              Ticket Not Open
                            </MenuItem>
                            <MenuItem value="cancelled" sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#991b1b' }}>
                              Cancel
                            </MenuItem>
                          </Select>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{fullName}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{trek?.name || '—'}</TableCell>
                        <TableCell sx={{ maxWidth: 280, verticalAlign: 'top' }}>
                          <div className="reg-cell-pkg-name">{pkg?.name || '—'}</div>
                          {pkgDescPreview ? (
                            <Tooltip title={pkgDescFull} placement="top" enterDelay={400}>
                              <div className="reg-cell-pkg-desc">{pkgDescPreview}</div>
                            </Tooltip>
                          ) : null}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(row.batch?.startDate)}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatCreatedAt(paidAt)}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              gap: 1,
                              minWidth: 52,
                              maxWidth: 72
                            }}
                          >
                            <span>{gender}</span>
                            <span>{age != null ? age : '—'}</span>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                            <span style={{ fontFamily: 'monospace', fontSize: '0.8rem', fontWeight: 600 }}>
                              {dpnr || '—'}
                            </span>
                            {latestUploadAt ? (
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: '0.68rem',
                                  color: '#059669',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.3
                                }}
                              >
                                <AccessTimeRoundedIcon sx={{ fontSize: '0.72rem' }} />
                                {formatCreatedAt(latestUploadAt)}
                              </Typography>
                            ) : null}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                            <span style={{ fontFamily: 'monospace', fontSize: '0.8rem', fontWeight: 600 }}>
                              {rpnr || '—'}
                            </span>
                            {latestUploadAt ? (
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: '0.68rem',
                                  color: '#059669',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.3
                                }}
                              >
                                <AccessTimeRoundedIcon sx={{ fontSize: '0.72rem' }} />
                                {formatCreatedAt(latestUploadAt)}
                              </Typography>
                            ) : null}
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<EditRoundedIcon sx={{ fontSize: '1rem !important' }} />}
                            sx={{
                              mr: 0.75,
                              borderRadius: '20px',
                              px: 1.5,
                              py: 0.35,
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              textTransform: 'none'
                            }}
                            onClick={() => openEditRegistration(row)}
                          >
                            Edit
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<CloudUploadRoundedIcon sx={{ fontSize: '1rem !important' }} />}
                            sx={{
                              borderRadius: '20px',
                              px: 1.5,
                              py: 0.35,
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              textTransform: 'none'
                            }}
                            onClick={() => openTicketGroupModal(row)}
                          >
                            Upload
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {(pagination.totalPages > 1 || total > 0) && !isLoading && list.length > 0 ? (
          <div className="ops-table-footer">
            <span className="ops-table-footer-info">
              Page {page} of {pagination.totalPages || 1}
              {total > 0 ? ` · ${total} total` : ''}
            </span>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" variant="outlined" disabled={page <= 1} onClick={() => setQuery({ page: String(page - 1) })}>
                Previous
              </Button>
              <Button
                size="small"
                variant="outlined"
                disabled={page >= (pagination.totalPages || 1)}
                onClick={() => setQuery({ page: String(page + 1) })}
              >
                Next
              </Button>
            </Box>
          </div>
        ) : null}
      </Card>

      <EditRegistrationModal
        open={editModal.open}
        bookingId={editModal.bookingId}
        onClose={() => setEditModal({ open: false, bookingId: null })}
        onSaved={() => queryClient.invalidateQueries('admin-ticket-bookings')}
      />

      <EditTicketGroupModal
        open={groupModal.open}
        row={groupModal.row}
        onClose={() => setGroupModal({ open: false, row: null })}
        onSaved={() => queryClient.invalidateQueries('admin-ticket-bookings')}
      />
    </div>
  );
};

export default Tickets;
